import { ForwardRefExoticComponent, RefAttributes } from 'react';

import { Image } from '@codes/ui-kit/components/ui/image';
import {
    Clipboard,
    Key,
    LucideProps,
    Presentation,
    ScrollText,
    Settings,
    Users,
} from 'lucide-react';

import {
    AiLogoDarkImage,
    AiLogoImage,
    BoxBlackSymbol,
    BoxWhiteSymbol,
    NavCatalogDarkIcon,
    NavCatalogIcon,
    NavServiceDarkIcon,
    NavServiceIcon,
} from '@codes/assets';

export interface MenuItem {
    icon?: (ForwardRefExoticComponent<Omit<LucideProps, 'ref'>> & RefAttributes<SVGSVGElement>) | (() => JSX.Element);
    label: string;
    hasDropdown?: boolean;
    href: string;
    subItems?: MenuItem[];
    noIconOpacity?: boolean;
}

// Path configuration for different route types
const PATH_TYPES = {
    BOX: '/box/',
    WORKSPACE_ONLY: ['/teams', '/my-page', '/openapi-keys'],
    SETTINGS: '/settings',
} as const;

const isWorkspaceOnlyPath = (path: string): boolean => {
    return PATH_TYPES.WORKSPACE_ONLY.some(
        workspaceOnlyPath => path === workspaceOnlyPath || path.startsWith(`${workspaceOnlyPath}/`)
    );
};

// Helper function to generate workspace/project-aware paths
export const getWorkspacePath = (path: string, workspaceId?: string, projectId?: string): string => {
    if (!workspaceId) {
        return path;
    }

    // Box paths remain unchanged
    if (path.startsWith(PATH_TYPES.BOX)) {
        return path;
    }

    // Workspace-only paths
    if (isWorkspaceOnlyPath(path)) {
        return `/w/${workspaceId}${path}`;
    }

    // Settings: context-dependent routing
    if (path === PATH_TYPES.SETTINGS) {
        const baseWorkspacePath = `/w/${workspaceId}`;
        return projectId ? `${baseWorkspacePath}/p/${projectId}${path}` : `${baseWorkspacePath}${path}`;
    }

    // Project-dependent paths
    if (projectId) {
        return `/w/${workspaceId}/p/${projectId}${path}`;
    }

    // Default to workspace path
    return `/w/${workspaceId}${path}`;
};

const createServiceMenuItem = (workspaceId?: string, projectId?: string): MenuItem => ({
    icon: () => <Image className="w-5 h-5" src={NavServiceIcon} darkSrc={NavServiceDarkIcon} />,
    label: 'menu.service',
    hasDropdown: false,
    href: projectId && workspaceId ? `/w/${workspaceId}/p/${projectId}` : workspaceId ? `/w/${workspaceId}` : '/w',
});

const createCatalogMenuItem = (workspaceId?: string, projectId?: string): MenuItem => ({
    icon: () => <Image className="w-5 h-5" src={NavCatalogIcon} darkSrc={NavCatalogDarkIcon} />,
    label: 'menu.catalog',
    hasDropdown: false,
    href: getWorkspacePath('/catalogs', workspaceId, projectId),
});

const createAiMenuItem = (workspaceId?: string, projectId?: string): MenuItem => ({
    icon: () => <Image className="w-[22px] h-[22px]" src={AiLogoImage} darkSrc={AiLogoDarkImage} />,
    label: 'menu.ai.default',
    hasDropdown: true,
    href: '',
    subItems: [
        { label: 'menu.ai.prompt', href: getWorkspacePath('/prompts', workspaceId, projectId) },
        { label: 'menu.ai.agent', href: getWorkspacePath('/my-agents', workspaceId, projectId) },
        { label: 'menu.ai.agentCatalog', href: getWorkspacePath('/agent-catalogs', workspaceId, projectId) },
    ],
});

const createBoxMenuItem = (): MenuItem => ({
    icon: () => <Image className="w-[22px] h-[22px]" src={BoxBlackSymbol} darkSrc={BoxWhiteSymbol} />,
    label: 'menu.box',
    hasDropdown: false,
    href: '/box/home',
    noIconOpacity: true,
});

const createMarketplaceMenuItem = (workspaceId?: string, projectId?: string): MenuItem => ({
    icon: Presentation,
    label: 'menu.marketplace',
    hasDropdown: false,
    href: getWorkspacePath('/marketplace', workspaceId, projectId),
});

const createTeamMenuItem = (workspaceId?: string): MenuItem => ({
    icon: Users,
    label: 'menu.team',
    hasDropdown: false,
    href: getWorkspacePath('/teams', workspaceId),
});

const createOpenApiKeysMenuItem = (workspaceId?: string): MenuItem => ({
    icon: Key,
    label: 'menu.openApiKeys',
    hasDropdown: false,
    href: getWorkspacePath('/openapi-keys', workspaceId),
});

const createLogsMenuItem = (workspaceId?: string, projectId?: string): MenuItem => ({
    icon: ScrollText,
    label: 'menu.logs',
    hasDropdown: false,
    href: getWorkspacePath('/logs', workspaceId, projectId),
});

const createSettingsMenuItem = (workspaceId?: string, projectId?: string): MenuItem => {
    // When project is selected, create dropdown menu with both settings options
    if (projectId) {
        return {
            icon: Settings,
            label: 'menu.settings',
            hasDropdown: true,
            href: '',
            subItems: [
                {
                    label: 'menu.workspaceSettings',
                    href: `/w/${workspaceId}/settings`,
                },
                {
                    label: 'menu.projectSettings',
                    href: `/w/${workspaceId}/p/${projectId}/settings`,
                },
            ],
        };
    }

    // When no project selected, direct link to workspace settings
    return {
        icon: Settings,
        label: 'menu.settings',
        hasDropdown: false,
        href: `/w/${workspaceId}/settings`,
    };
};

export const getMenuItems = (workspaceId?: string, projectId?: string): MenuItem[] => {
    const baseMenuItems: MenuItem[] = [
        createServiceMenuItem(workspaceId, projectId),
        createCatalogMenuItem(workspaceId, projectId),
    ];

    const projectMenuItems: MenuItem[] = projectId
        ? [
              createMarketplaceMenuItem(workspaceId, projectId),
              createAiMenuItem(workspaceId, projectId),
              createBoxMenuItem(),
          ]
        : [];

    const commonMenuItems: MenuItem[] = [createTeamMenuItem(workspaceId), createOpenApiKeysMenuItem(workspaceId)];

    const devMenuItems: MenuItem[] =
        import.meta.env.VITE_ENV?.trim().toUpperCase() !== 'PROD' ? [createLogsMenuItem(workspaceId, projectId)] : [];

    return [
        ...baseMenuItems,
        ...projectMenuItems,
        ...commonMenuItems,
        ...devMenuItems,
        createSettingsMenuItem(workspaceId, projectId),
    ];
};

export const getLightMenuItems = (workspaceId?: string): MenuItem[] => [
    {
        icon: Clipboard,
        label: 'menu.project',
        hasDropdown: false,
        href: workspaceId ? `/w/${workspaceId}` : '/w',
    },
    createTeamMenuItem(workspaceId),
    createOpenApiKeysMenuItem(workspaceId),
    createSettingsMenuItem(workspaceId),
];

// Legacy exports for backward compatibility
export const menuItems: MenuItem[] = getMenuItems();
export const lightMenuItems: MenuItem[] = getLightMenuItems();
